import React, { useMemo, useState } from 'react';
import { SlidersHorizontal, Calculator, Play, Loader2, Check } from 'lucide-react';
type SimState = 'idle' | 'running' | 'complete';
export function ScenarioBuilder() {
  const [fico, setFico] = useState(620);
  const [loanAmount, setLoanAmount] = useState(250);
  const [loanType, setLoanType] = useState('All Types');
  const [creditRun, setCreditRun] = useState('Any');
  const [simState, setSimState] = useState<SimState>('idle');
  const projectedImpact = useMemo(() => {
    const ficoFactor = Math.max(0, 850 - fico) * 45;
    const amountFactor = Math.max(0, 1000 - loanAmount) * 18;
    const typeFactor = loanType === 'All Types' ? 1 : 0.42;
    const creditFactor = creditRun === 'True' ? 0.65 : creditRun === 'False' ? 0.35 : 1;
    return Math.max(120, Math.floor((ficoFactor + amountFactor) * typeFactor * creditFactor));
  }, [fico, loanAmount, loanType, creditRun]);
  const newLeads = Math.floor(projectedImpact * 0.12);
  const handleRun = () => {
    if (simState !== 'idle') return;
    setSimState('running');
    setTimeout(() => {
      setSimState('complete');
      setTimeout(() => setSimState('idle'), 1800);
    }, 1500);
  };
  const buttonStyles = simState === 'complete' ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20' : 'bg-violet-500 hover:bg-violet-600 shadow-violet-500/20';
  return <div className="bg-slate-900/80 border border-slate-800/60 rounded-xl p-5 flex flex-col h-full relative overflow-hidden" data-id="element-260">
      <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-261"></div>

      <div className="flex items-center justify-between mb-6 relative z-10" data-id="element-262">
        <div className="flex items-center gap-2" data-id="element-263">
          <div className="p-2 bg-violet-500/10 rounded-lg" data-id="element-264">
            <SlidersHorizontal className="w-5 h-5 text-violet-400" data-id="element-265" />
          </div>
          <div data-id="element-266">
            <h2 className="text-lg font-semibold text-white" data-id="element-267">
              Scenario Builder
            </h2>
            <p className="text-sm text-slate-400" data-id="element-268">
              "What happens if we change X?"
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-5 flex-1 relative z-10" data-id="element-269">
        <div data-id="element-270">
          <div className="flex justify-between items-center mb-2" data-id="element-271">
            <label htmlFor="fico-range" className="text-sm font-medium text-slate-300" data-id="element-272">
              Minimum FICO Score
            </label>
            <span className="text-sm font-bold text-white tabular-nums" data-id="element-273">
              {fico}
            </span>
          </div>
          <input id="fico-range" type="range" min={500} max={800} step={10} value={fico} onChange={e => setFico(parseInt(e.target.value))} className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-violet-500" data-id="element-274" />
          <div className="flex justify-between text-xs text-slate-500 mt-1 tabular-nums" data-id="element-275">
            <span data-id="element-276">500</span>
            <span data-id="element-277">800</span>
          </div>
        </div>

        <div data-id="element-278">
          <div className="flex justify-between items-center mb-2" data-id="element-279">
            <label htmlFor="loan-range" className="text-sm font-medium text-slate-300" data-id="element-280">
              Min Loan Amount
            </label>
            <span className="text-sm font-bold text-white tabular-nums" data-id="element-281">
              ${loanAmount}k
            </span>
          </div>
          <input id="loan-range" type="range" min={100} max={1000} step={50} value={loanAmount} onChange={e => setLoanAmount(parseInt(e.target.value))} className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-violet-500" data-id="element-282" />
          <div className="flex justify-between text-xs text-slate-500 mt-1 tabular-nums" data-id="element-283">
            <span data-id="element-284">$100k</span>
            <span data-id="element-285">$1M+</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3" data-id="element-286">
          <div data-id="element-287">
            <label htmlFor="loan-type" className="text-xs font-medium text-slate-400 block mb-1.5" data-id="element-288">
              Loan Type
            </label>
            <select id="loan-type" value={loanType} onChange={e => setLoanType(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/50 cursor-pointer" data-id="element-289">
              <option data-id="element-290">All Types</option>
              <option data-id="element-291">VA Cash Out</option>
              <option data-id="element-292">FHA</option>
              <option data-id="element-293">Conventional</option>
            </select>
          </div>
          <div data-id="element-294">
            <label htmlFor="credit-run" className="text-xs font-medium text-slate-400 block mb-1.5" data-id="element-295">
              Credit Run
            </label>
            <select id="credit-run" value={creditRun} onChange={e => setCreditRun(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/50 cursor-pointer" data-id="element-296">
              <option data-id="element-297">Any</option>
              <option data-id="element-298">True</option>
              <option data-id="element-299">False</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-slate-800/60 relative z-10" data-id="element-300">
        <div className="flex items-center justify-between mb-4" data-id="element-301">
          <div className="flex items-center gap-2 text-slate-400 text-sm" data-id="element-302">
            <Calculator className="w-4 h-4" data-id="element-303" />
            Projected Impact
          </div>
          <div className="text-right" data-id="element-304">
            <div className="text-2xl font-bold text-white tabular-nums" data-id="element-305">
              ~{projectedImpact.toLocaleString()}
            </div>
            <div className="text-xs text-emerald-400 font-medium tabular-nums" data-id="element-306">
              +{newLeads.toLocaleString()} new leads
            </div>
          </div>
        </div>

        <button onClick={handleRun} disabled={simState !== 'idle'} className={`w-full py-2.5 ${buttonStyles} disabled:opacity-90 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 shadow-lg`} data-id="element-307">
          {simState === 'idle' && <>
              <Play className="w-4 h-4 fill-current" data-id="element-308" />
              Run Full Simulation
            </>}
          {simState === 'running' && <>
              <Loader2 className="w-4 h-4 animate-spin" data-id="element-309" />
              Running Simulation...
            </>}
          {simState === 'complete' && <>
              <Check className="w-4 h-4" data-id="element-310" />
              Simulation Complete
            </>}
        </button>
      </div>
    </div>;
}